import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, PoundSterling, Clock, TrendingUp, ArrowRight } from "lucide-react";

/**
 * Public-access ROI calculator for boardroom pitches.
 *
 * Built around a single insight: directors approve £2,500 onboarding + the
 * monthly platform fee + £5/sold job on the basis of *their* numbers, not a
 * generic 38% case study. This page lets them plug in 4 inputs from their
 * own dealership and see a concrete pound-figure projection plus payback-in-days.
 *
 * No login required (route mounted in App.js as /roi-calculator) — Steve can
 * email the link mid-conversation and the director plays with it on the spot.
 *
 * Math (deliberately conservative — boardroom-defensible):
 *   - Baseline approved-extras revenue/month = avgExtras × monthlyJobs × conversionRate
 *   - Projected uplift = baseline × upliftFactor where upliftFactor scales
 *     between 0.15 and 0.42 based on current conversion (lower conversion =
 *     more headroom). Documented inline below so directors can audit it.
 *   - Service plan add = monthlyJobs × spAdoption × £5 commission to dealer
 *   - Onboarding fee + £500/month platform fee + £5/sold-job fee modelled
 *     to compute payback days
 */

const ONBOARDING_FEE_GBP = 1000;
const PLATFORM_FEE_MONTHLY_GBP = 1500;         // £/month flat SaaS subscription (Option A)
const CONTRACT_TERM_MONTHS = 12;               // minimum term (both options)
const COMMISSION_RATE = 0.03;                  // Option B: 3% on customer-approved extras
const SERVICE_PLAN_COMMISSION_GBP = 5;         // dealer keeps the £5 per plan sold

// Uplift envelope — well-tested in our test cohort. Conservative end of the
// industry range published by VHC-vendor case studies.
const MIN_UPLIFT = 0.15;
const MAX_UPLIFT = 0.42;

function gbp(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Math.max(0, n));
}

function NumberField({ label, value, onChange, suffix, hint, min = 0, max = 100000, step = 1, testId }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-white/60">
        {label}
      </label>
      <div className="mt-1.5 flex items-center rounded-md bg-white/5 border border-white/15 focus-within:border-primary transition-colors">
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          min={min} max={max} step={step}
          className="w-full px-3 py-2 bg-transparent text-white text-base focus:outline-none"
          data-testid={testId}
        />
        {suffix && <span className="px-3 text-sm font-mono text-white/50">{suffix}</span>}
      </div>
      {hint && <p className="text-[10.5px] text-white/40 mt-1">{hint}</p>}
    </div>
  );
}

function ResultTile({ label, value, sub, tone = "default", icon: Icon, testId }) {
  const toneCls = tone === "primary"
    ? "border-primary/40 bg-primary/[0.08]"
    : tone === "success"
      ? "border-success/40 bg-success/[0.08]"
      : "border-white/10 bg-white/[0.02]";
  return (
    <div className={`rounded-lg border p-5 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{label}</span>
      </div>
      <div className="mt-1 font-display font-bold text-3xl text-white">{value}</div>
      {sub && <div className="text-[11px] text-white/60 mt-1">{sub}</div>}
    </div>
  );
}

export default function RoiCalculatorPage() {
  // Steve — pre-Arran-meeting (Feb 2026). The full calculator is temporarily
  // replaced with a "Pricing TBA" placeholder so no £ figures leak pre-pilot.
  // Restore by exporting RoiCalculatorPageOriginal below instead.
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-white/10 sticky top-0 z-30 backdrop-blur bg-background/85">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> ServiceSell PRO
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-wider text-primary border border-primary/40 rounded px-1.5 py-0.5">
            ROI CALCULATOR
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-24 text-center space-y-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/[0.06] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">
          <Calculator className="w-3.5 h-3.5" /> Coming after the pilot
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
          Pricing to be confirmed.
        </h1>
        <p className="text-base text-white/70 leading-relaxed max-w-xl mx-auto">
          The live ROI calculator returns as soon as we&apos;ve finalised pilot pricing with our founding dealer group.
          In the meantime, ask us for a bespoke projection using your own dealership&apos;s numbers.
        </p>
        <div className="pt-2">
          <a href="mailto:info@servicesellpro.co.uk" className="btn-primary">
            Request a bespoke projection <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
    </div>
  );
}

// Original full calculator — preserved for post-pilot restore. Export this as
// the default (rename above) once pricing is finalised.
function RoiCalculatorPageOriginal() {
  // Sensible Toyota-franchised-dealer defaults — a director will see roughly
  // the right ballpark on page load even before they tweak anything.
  const [monthlyJobs, setMonthlyJobs] = useState(280);
  const [avgExtras, setAvgExtras] = useState(180);
  const [conversion, setConversion] = useState(32);   // % currently authorising extras
  const [spAdoption, setSpAdoption] = useState(8);    // % of customers taking a service plan offer
  // Pricing-tier toggle — boardroom can flip between Option A (subscription)
  // and Option B (3% commission) live during the pitch.
  const [pricingTier, setPricingTier] = useState("subscription");  // "subscription" | "commission"

  const inputs = useMemo(() => ({
    monthlyJobs: Number(monthlyJobs) || 0,
    avgExtras: Number(avgExtras) || 0,
    conversion: Math.max(0, Math.min(100, Number(conversion) || 0)),
    spAdoption: Math.max(0, Math.min(100, Number(spAdoption) || 0)),
  }), [monthlyJobs, avgExtras, conversion, spAdoption]);

  const projection = useMemo(() => {
    const jobsPerMonth = inputs.monthlyJobs;
    const baselineMonthlyExtras = jobsPerMonth * inputs.avgExtras * (inputs.conversion / 100);
    // Uplift curve — lower conversion = more headroom. 20% conversion gets
    // closer to 42% uplift; 50%+ conversion gets closer to 15%.
    const conversionGap = Math.max(0, 60 - inputs.conversion) / 60;  // 0..1
    const upliftFactor = MIN_UPLIFT + conversionGap * (MAX_UPLIFT - MIN_UPLIFT);
    const monthlyUpliftRevenue = baselineMonthlyExtras * upliftFactor;

    // Total approved customer-facing extras revenue once ServiceSell PRO is
    // live (baseline + AI-driven uplift). This is the figure on which
    // Option B's 3% commission is applied.
    const totalApprovedExtrasPostSSP = baselineMonthlyExtras + monthlyUpliftRevenue;

    // ---------- Option A: SaaS subscription (£1,500/mo flat) ----------
    const subscriptionMonthlyFee = PLATFORM_FEE_MONTHLY_GBP;
    const subscriptionDealerNet = monthlyUpliftRevenue - PLATFORM_FEE_MONTHLY_GBP;
    const subscriptionRoiMultiple = PLATFORM_FEE_MONTHLY_GBP > 0
      ? monthlyUpliftRevenue / PLATFORM_FEE_MONTHLY_GBP
      : 0;
    const subscriptionAnnualFees = PLATFORM_FEE_MONTHLY_GBP * CONTRACT_TERM_MONTHS + ONBOARDING_FEE_GBP;
    const subscriptionAnnualNet = subscriptionDealerNet * CONTRACT_TERM_MONTHS - ONBOARDING_FEE_GBP;
    const subscriptionPayback = subscriptionDealerNet > 0
      ? Math.ceil((ONBOARDING_FEE_GBP / subscriptionDealerNet) * 30)
      : Infinity;

    // ---------- Option B: 3% commission on customer-approved extras ----------
    // Dealer pays nothing if Platform sells nothing. Lower upside but lower
    // floor — risk transferred to ServiceSell PRO.
    const commissionMonthlyFee = totalApprovedExtrasPostSSP * COMMISSION_RATE;
    const commissionDealerNet = monthlyUpliftRevenue - commissionMonthlyFee;
    const commissionRoiMultiple = commissionMonthlyFee > 0
      ? monthlyUpliftRevenue / commissionMonthlyFee
      : 0;
    const commissionAnnualFees = commissionMonthlyFee * CONTRACT_TERM_MONTHS + ONBOARDING_FEE_GBP;
    const commissionAnnualNet = commissionDealerNet * CONTRACT_TERM_MONTHS - ONBOARDING_FEE_GBP;
    const commissionPayback = commissionDealerNet > 0
      ? Math.ceil((ONBOARDING_FEE_GBP / commissionDealerNet) * 30)
      : Infinity;

    // Surface whichever the user has selected
    const isSubscription = pricingTier === "subscription";
    const monthlySSPCommission = isSubscription ? subscriptionMonthlyFee : commissionMonthlyFee;
    const monthlyDealerNet = isSubscription ? subscriptionDealerNet : commissionDealerNet;
    const monthlyRoiMultiple = isSubscription ? subscriptionRoiMultiple : commissionRoiMultiple;
    const annualFeesPaid = isSubscription ? subscriptionAnnualFees : commissionAnnualFees;
    const annualDealerNet = isSubscription ? subscriptionAnnualNet : commissionAnnualNet;
    const paybackDays = isSubscription ? subscriptionPayback : commissionPayback;

    // Service plan revenue (same for both options — separate B2B referral)
    const monthlyPlansSold = jobsPerMonth * (inputs.spAdoption / 100);
    const monthlySPCommission = monthlyPlansSold * SERVICE_PLAN_COMMISSION_GBP;

    const monthlyFee = monthlySSPCommission + monthlySPCommission;
    const quarterlyDealerNet = monthlyDealerNet * 3;

    return {
      baselineMonthlyExtras,
      monthlyUpliftRevenue,
      monthlyDealerNet,
      monthlySSPCommission,
      monthlySPCommission,
      monthlyFee,
      quarterlyDealerNet,
      annualFeesPaid,
      annualDealerNet,
      monthlyRoiMultiple,
      paybackDays,
      upliftFactor,
      pricingTier,
      // Side-by-side view for the boardroom — both Options visible at once
      sideBySide: {
        subscription: {
          monthlyFee: subscriptionMonthlyFee,
          monthlyNet: subscriptionDealerNet,
          roi: subscriptionRoiMultiple,
          annualNet: subscriptionAnnualNet,
        },
        commission: {
          monthlyFee: commissionMonthlyFee,
          monthlyNet: commissionDealerNet,
          roi: commissionRoiMultiple,
          annualNet: commissionAnnualNet,
        },
      },
    };
  }, [inputs, pricingTier]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="roi-calculator-page">
      <header className="border-b border-white/10 sticky top-0 z-10 backdrop-blur bg-background/80">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white" data-testid="roi-back-home">
            <ArrowLeft className="w-4 h-4" /> ServiceSell PRO
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-wider text-primary border border-primary/40 rounded px-1.5 py-0.5">
            ROI CALCULATOR
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Hero */}
        <section className="space-y-4 max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary">
            Your dealership, your numbers
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-balance leading-[1.05]">
            Plug in your numbers.<br />
            <span className="text-primary">See exactly what ServiceSell PRO returns.</span>
          </h1>
          <p className="text-base text-white/70 leading-relaxed">
            No generic case studies. The four inputs below are the only ones that matter — and we model the rest live on
            this page. Conservative uplift envelope (15-42%), commission already netted off, payback in days, not years.
          </p>
        </section>

        {/* Inputs + outputs grid */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <h2 className="font-display font-bold text-xl flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary" /> Your dealership
            </h2>
            <NumberField
              label="Monthly job volume"
              value={monthlyJobs}
              onChange={setMonthlyJobs}
              suffix="jobs / mo"
              hint="Total customer-facing jobs through your aftersales department per month."
              max={5000}
              testId="roi-input-monthly-jobs"
            />
            <NumberField
              label="Average extras value per job"
              value={avgExtras}
              onChange={setAvgExtras}
              suffix="£"
              hint="The typical £ value of recommended additional work you quote (beyond the booked-in job)."
              max={5000}
              testId="roi-input-avg-extras"
            />
            <NumberField
              label="Current extras conversion"
              value={conversion}
              onChange={setConversion}
              suffix="%"
              hint="What % of those extras the customer authorises today (industry benchmark: 28-35%)."
              max={100}
              testId="roi-input-conversion"
            />
            <NumberField
              label="Service plan adoption"
              value={spAdoption}
              onChange={setSpAdoption}
              suffix="%"
              hint="% of customers who take up a service plan offer when presented."
              max={100}
              step={0.5}
              testId="roi-input-sp-adoption"
            />

            <div className="rounded-md border border-white/10 bg-white/[0.02] p-4 text-[11px] text-white/55 leading-relaxed">
              <strong className="text-white/80">How we model uplift.</strong> Lower current conversion = more headroom.
              At 20% you sit near 42% uplift; at 50% you sit near 15%. Both ends are conservative versus published
              VHC-vendor case studies. Your modelled uplift today: <span className="font-mono text-primary">{(projection.upliftFactor * 100).toFixed(0)}%</span>
              of baseline extras revenue.
            </div>
          </div>

          {/* Outputs */}
          <div className="space-y-4">
            <h2 className="font-display font-bold text-xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Your projected return
            </h2>

            {/* Pricing-tier toggle — boardroom can flip live */}
            <div className="rounded-lg border border-white/15 bg-white/[0.03] p-1.5 grid grid-cols-2 gap-1.5" data-testid="roi-pricing-tier-toggle">
              <button
                type="button"
                onClick={() => setPricingTier("subscription")}
                className={`px-3 py-2.5 rounded-md text-xs font-bold transition-colors ${pricingTier === "subscription" ? "bg-primary text-primary-foreground" : "text-white/65 hover:text-white"}`}
                data-testid="roi-tier-subscription"
              >
                <div>Option A: SaaS subscription</div>
                <div className={`text-[10px] font-normal mt-0.5 ${pricingTier === "subscription" ? "opacity-90" : "opacity-60"}`}>
                  £{ONBOARDING_FEE_GBP.toLocaleString()} setup + £{PLATFORM_FEE_MONTHLY_GBP.toLocaleString()}/mo
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPricingTier("commission")}
                className={`px-3 py-2.5 rounded-md text-xs font-bold transition-colors ${pricingTier === "commission" ? "bg-primary text-primary-foreground" : "text-white/65 hover:text-white"}`}
                data-testid="roi-tier-commission"
              >
                <div>Option B: 3% commission</div>
                <div className={`text-[10px] font-normal mt-0.5 ${pricingTier === "commission" ? "opacity-90" : "opacity-60"}`}>
                  £{ONBOARDING_FEE_GBP.toLocaleString()} setup + {(COMMISSION_RATE * 100).toFixed(0)}% of approved extras
                </div>
              </button>
            </div>

            <ResultTile
              label="Net to your dealership · monthly"
              value={gbp(projection.monthlyDealerNet)}
              sub={pricingTier === "subscription"
                ? `Approved-extras revenue minus your ${gbp(PLATFORM_FEE_MONTHLY_GBP)}/month subscription`
                : `Approved-extras revenue minus the ${(COMMISSION_RATE * 100).toFixed(0)}% commission on customer-authorised extras`}
              tone="primary"
              icon={PoundSterling}
              testId="roi-out-monthly-net"
            />
            <ResultTile
              label="ROI multiple · monthly"
              value={`${projection.monthlyRoiMultiple.toFixed(1)}x`}
              sub={`Every \u00a31 you pay ServiceSell PRO\u2122 returns \u00a3${projection.monthlyRoiMultiple.toFixed(1)} in approved customer-facing revenue.`}
              tone="success"
              icon={TrendingUp}
              testId="roi-out-monthly-roi"
            />
            <ResultTile
              label={`Net over your 12-month term`}
              value={gbp(projection.annualDealerNet)}
              sub={pricingTier === "subscription"
                ? `After ${gbp(PLATFORM_FEE_MONTHLY_GBP * CONTRACT_TERM_MONTHS)} subscription + ${gbp(ONBOARDING_FEE_GBP)} onboarding`
                : `After ${(COMMISSION_RATE * 100).toFixed(0)}% commission + ${gbp(ONBOARDING_FEE_GBP)} onboarding`}
              tone="success"
              icon={PoundSterling}
              testId="roi-out-annual-net"
            />
            <ResultTile
              label={`Payback on the \u00a3${ONBOARDING_FEE_GBP.toLocaleString()} onboarding`}
              value={projection.paybackDays === Infinity ? "—" : `${projection.paybackDays} days`}
              sub="From the moment your first quote goes out."
              icon={Clock}
              testId="roi-out-payback"
            />

            {/* Side-by-side compare — both options on screen at once for the boardroom */}
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 space-y-3" data-testid="roi-side-by-side">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.25em] text-primary">Side-by-side · 12-month view</div>
                <div className="text-[10px] text-white/45">{gbp(projection.baselineMonthlyExtras + projection.monthlyUpliftRevenue)} of approved extras / mo</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white/55">Option A · Subscription</div>
                  <div className="flex justify-between"><span className="text-white/60">Your monthly cost</span><span className="font-mono text-white/90">{gbp(projection.sideBySide.subscription.monthlyFee)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Your monthly net</span><span className="font-mono text-success">{gbp(projection.sideBySide.subscription.monthlyNet)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">ROI multiple</span><span className="font-mono text-white/90">{projection.sideBySide.subscription.roi.toFixed(1)}x</span></div>
                  <div className="flex justify-between font-bold pt-1 border-t border-white/10"><span className="text-white">12-month net</span><span className="font-mono text-success">{gbp(projection.sideBySide.subscription.annualNet)}</span></div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white/55">Option B · 3% Commission</div>
                  <div className="flex justify-between"><span className="text-white/60">Your monthly cost</span><span className="font-mono text-white/90">{gbp(projection.sideBySide.commission.monthlyFee)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Your monthly net</span><span className="font-mono text-success">{gbp(projection.sideBySide.commission.monthlyNet)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">ROI multiple</span><span className="font-mono text-white/90">{projection.sideBySide.commission.roi.toFixed(1)}x</span></div>
                  <div className="flex justify-between font-bold pt-1 border-t border-white/10"><span className="text-white">12-month net</span><span className="font-mono text-success">{gbp(projection.sideBySide.commission.annualNet)}</span></div>
                </div>
              </div>
              <div className="text-[10.5px] text-white/55 italic leading-relaxed pt-1 border-t border-white/10">
                Pick the model that fits your appetite for predictability vs. pay-as-you-sell. We have margin to negotiate &mdash; tell us what works.
              </div>
            </div>

            {/* Cost transparency line */}
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-4 text-[12px] text-white/65 leading-relaxed space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span>Baseline extras revenue today</span>
                <span className="font-mono text-white/85">{gbp(projection.baselineMonthlyExtras)} / mo</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span>Modelled uplift</span>
                <span className="font-mono text-white/85">+{gbp(projection.monthlyUpliftRevenue)} / mo</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span>{pricingTier === "subscription" ? "ServiceSell PRO\u2122 subscription (all-inclusive)" : `ServiceSell PRO\u2122 commission (${(COMMISSION_RATE * 100).toFixed(0)}% of approved extras)`}</span>
                <span className="font-mono text-white/85">− {gbp(projection.monthlySSPCommission)} / mo</span>
              </div>
              <div className="flex items-baseline justify-between gap-2 text-success">
                <span>Plus service plan commission (your £5 per plan sold)</span>
                <span className="font-mono">+{gbp(projection.monthlySPCommission)} / mo</span>
              </div>
              <div className="border-t border-white/10 my-2" />
              <div className="flex items-baseline justify-between gap-2 font-bold text-white">
                <span>Net to your dealership</span>
                <span className="font-mono">{gbp(projection.monthlyDealerNet + projection.monthlySPCommission)} / mo</span>
              </div>
              <div className="pt-2 mt-2 border-t border-white/5 text-[10.5px] text-white/45 italic">
                12-month minimum term. Monthly ROI tracked inside the app — your renewal conversation in month 12 writes itself.
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] p-8 text-center" data-testid="roi-cta-section">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary">Next step</div>
          <h2 className="mt-2 font-display font-black text-3xl text-balance">
            Try it free for 30 days. Keep your data either way.
          </h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">
            We&apos;ll seed your last 100 jobs, train your advisor team in 90 minutes, and you decide on day 31 whether to keep going.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/pilot" className="btn-primary" data-testid="roi-cta-pilot">
              See pilot terms <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:steve.kirby21@gmail.com?subject=ServiceSell%20PRO%20pilot%20enquiry" className="btn-outline" data-testid="roi-cta-email">
              Talk to Steve
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-6xl mx-auto px-6 py-6 text-[11px] text-white/40">
          ServiceSellPro Limited · Registered in England &amp; Wales · Company No. 17211707 · ICO ZC144538 · Registered UK trade mark No. UK00004398263
        </div>
      </footer>
    </div>
  );
}
